import { Component, HostBinding, Input, OnDestroy, OnInit, HostListener, inject } from '@angular/core';
import {BehaviorSubject, combineLatest, of, Subscription} from 'rxjs';
import {ProjectReferenceService} from '../../services/project-reference.service';
import {first, map, shareReplay, switchMap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {ProjectReferenceSearchResponse} from '../../models/project-reference-search-response';

const carouselVisibleItemCount = 5;

@Component({
    selector: 'app-project-reference-carousel',
    templateUrl: './project-reference-carousel.component.html',
    standalone: false
})
export class ProjectReferenceCarouselComponent implements OnInit, OnDestroy {
    private projectReferenceService = inject<ProjectReferenceService>('ProjectReferenceService' as any);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    private subscriptions: Subscription[] = [];
    private idsSubject$ = new BehaviorSubject<number[]>([]);

    visibleReferences: ProjectReferenceSearchResponse[] = [];
    references$ = this.idsSubject$.pipe(
        switchMap(ids => {
            if (ids.length === 0) {
                return of([])
            }

            return this.projectReferenceService.listReferences('', {id: ids}, 0).pipe(
                map(response => response.content.sort(((a, b) => {
                    // Sort references by order in original array
                    const aIndex = ids.indexOf(a.id);
                    const bIndex = ids.indexOf(b.id);
                    if (aIndex < bIndex) {
                        return -1;
                    } else if (bIndex > aIndex) {
                        return 1;
                    } else {
                        return 0;
                    }
                })))
            );
        }),
        shareReplay({ bufferSize: 1, refCount: true})
    );

    @HostBinding('class')
    classes = 'block'

    @Input() set ids(ids: number[]) {
        this.idsSubject$.next(ids)
    }

    @Input() dark: boolean = false;

    mobile = false;

    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
        this.checkMobile()
    }

    checkMobile(){
        this.mobile = window.screen.width <= 450;
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.references$,
                this.route.params.pipe(map(params => params['id']))
            ]).subscribe(([references, currentId]) => {
                const selectedItemIndex = references.findIndex(item => item.id === +currentId);
                const itemPosition = selectedItemIndex !== -1
                    ? selectedItemIndex % carouselVisibleItemCount
                    : 0;
                let startIndex = selectedItemIndex !== -1
                    ? selectedItemIndex - itemPosition
                    : 0;

                this.visibleReferences = references.length < carouselVisibleItemCount
                    ? references
                    : this.getVisibleReferences(references, startIndex);
            })
        )
        this.checkMobile()
    }

    ngOnDestroy() {
        this.subscriptions.forEach(it => it.unsubscribe());
    }

    async selectPreviousItem() {
        const references = await this.getReferences();
        const currentIndex = await this.getCurrentIndex(references);
        const previousIndex = (currentIndex - 1) < 0
            ? references.length - 1
            : currentIndex - 1;
        this.navigateToCarouselItem(references[previousIndex]);
    }

    async selectNextItem() {
        const references = await this.getReferences();
        const currentIndex = await this.getCurrentIndex(references);
        const nextIndex = (currentIndex + 1) >= references.length
            ? 0
            : currentIndex + 1;
        this.navigateToCarouselItem(references[nextIndex]);
    }

    private async getReferences() {
        return await this.references$.pipe(first()).toPromise();
    }

    private async getCurrentIndex(references: ProjectReferenceSearchResponse[]) {
        const id = await this.route.params.pipe(map(params => params['id']), first()).toPromise();
        return references.findIndex(item => item.id === +id);
    }

    private navigateToCarouselItem(item: ProjectReferenceSearchResponse) {
        this.router.navigate(
            ['/project-references', item.id],
            {queryParamsHandling: 'preserve', replaceUrl: true}
        );
    }

    private getVisibleReferences(references: ProjectReferenceSearchResponse[], startIndex: number) {
        let refs: ProjectReferenceSearchResponse[] = [];
        const start = startIndex > references.length
            ? startIndex - references.length
            : startIndex;

        if (start + carouselVisibleItemCount < references.length) {
            refs = references.slice(start, carouselVisibleItemCount);
        } else {
            refs = [
                ...references.slice(start),
                ...references.slice(0, carouselVisibleItemCount - (references.length - start))
            ];
        }
        return refs;
    }
}
